import React, { useState, useMemo, useCallback } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { ssnStatusCodeFilters } from "../filterConfigurations";
import SimpleToolbar from "../../common/SimpleToolbar";

const SsnStatusCode = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["SSN Status Code"]?.edit;
  const {
    data: terms,
    loading,
    error,
  } = useFirestore("SsnStatusCode", "SSN_Status_Code", "asc");
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  // Use custom filter hook
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(terms || [], ssnStatusCodeFilters);

  const pageName = "SSN Status Code";

  const columns = useMemo(
    () => [
      { field: "SSN_Status", headerName: "SSN Status", flex: 1, minWidth: 150 },
      {
        field: "SSN_Status_Code",
        headerName: "SSN Status Code",
        flex: 1,
        minWidth: 150,
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((term, index) => ({
      id: term.id || index, // Use term.id if available, otherwise use index
      ...term,
    }));
  }, [filteredData]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Simple toolbar without search (search is external)
  const CustomToolbar = useCallback(() => {
    return (
      <SimpleToolbar
        exportOptions={{
          csvOptions: {
            fileName: "ssn-status-codes",
            delimiter: ",",
          },
          printOptions: {
            hideFooter: true,
            hideToolbar: true,
          },
        }}
      />
    );
  }, []);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedTerm(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedTerm(null);
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    // Data will refresh automatically due to onSnapshot in useFirestore
    // Snackbar for add will be handled in Add.js
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching SSN Status Codes:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={ssnStatusCodeFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={{}}
          title="SSN Status Code Filters"
          resultCount={filteredData.length}
          totalCount={terms?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="SsnStatusCode"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={terms}
              templateFields={columns.map((col) => col.field)}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="SsnStatusCode" />
          )}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: CustomToolbar }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedTerm && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          termDetail={selectedTerm}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SsnStatusCode;
